import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { toast } from 'react-toastify'
import { useBeforeUnload } from 'react-use'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { errorToMessages, type GraduatesFilters } from './query'

const POLL_INTERVAL_MS = 3000

interface Props {
  readonly filters: GraduatesFilters
}

interface CurrentExport {
  readonly exportId: string
  /** Filters the file was made for; a different filter needs a new file. */
  readonly filtersKey: string
}

/** Excel is built in the background: start → poll the status → open the download link. */
export default function GraduatesExportButton({ filters }: Props): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const [current, setCurrent] = React.useState<CurrentExport | null>(null)
  const [isStarting, setIsStarting] = React.useState(false)
  const downloadedExportId = React.useRef<string | null>(null)
  const filtersKey = JSON.stringify(filters)

  const showError = React.useCallback(
    (error: unknown) => {
      const messages = errorToMessages(error)
      toast.error(messages.length > 0 ? messages.join('\n') : t('error:an_error_occurred'))
    },
    [t]
  )

  const {
    data: status,
    error,
    mutate,
  } = useApi({
    endpoint: Api.getAdministratorStatisticsGraduatesExport,
    params: React.useMemo(
      () => ({ headers, args: { exportId: current?.exportId ?? '' } }),
      [headers, current?.exportId]
    ),
    shouldFetch: current != null,
    suspense: false,
    refreshInterval: (latest?: Api.AdministratorGraduatesExport | null) =>
      latest == null || latest.status === 'pending' ? POLL_INTERVAL_MS : 0,
    refreshWhenHidden: true,
  })

  // 404: the export is gone, start over
  React.useEffect(() => {
    if (error != null) {
      showError(error)
      setCurrent(null)
    }
  }, [error, showError])

  const download = React.useCallback(async (): Promise<void> => {
    let latest: Api.AdministratorGraduatesExport | null | undefined = status
    // The link is valid for 60 minutes; asking for the status again gives a fresh one
    if (latest?.expiresAt != null && Date.parse(latest.expiresAt) <= Date.now()) {
      latest = await mutate()
    }
    if (latest?.downloadUrl != null) {
      location.assign(latest.downloadUrl)
    }
  }, [status, mutate])

  React.useEffect(() => {
    if (status?.status === 'done' && current != null && downloadedExportId.current !== current.exportId) {
      downloadedExportId.current = current.exportId
      void download()
    }
    if (status?.status === 'failed' && current != null) {
      toast.error(status.message ?? t('error:an_error_occurred'))
      setCurrent(null)
    }
  }, [status, current, download, t])

  const start = React.useCallback(async (): Promise<void> => {
    setIsStarting(true)
    try {
      const created = await Api.postAdministratorStatisticsGraduatesExport({ headers, body: filters })
      setCurrent({ exportId: created.exportId, filtersKey })
    } catch (err) {
      // 422: the file would be too large, the filter has to be narrowed
      showError(err)
    } finally {
      setIsStarting(false)
    }
  }, [headers, filters, filtersKey, showError])

  const isPending = isStarting || (current != null && status?.status !== 'done')
  const isReady = current != null && current.filtersKey === filtersKey && status?.status === 'done'

  // Leaving the page while the file is being prepared loses the download
  useBeforeUnload(isPending, t('statistics:file_preparing'))

  return (
    <Button variant="blue" type="button" disabled={isPending} onClick={() => void (isReady ? download() : start())}>
      <FontAwesomeIcon
        icon={isPending ? ProRegularSvgIcons.faSpinner : ProRegularSvgIcons.faFileExcel}
        spin={isPending}
        className="mr-2"
      />
      {isPending ? t('statistics:file_preparing') : t('statistics:download_excel')}
    </Button>
  )
}
